import { Text } from '@user-portal/wikr-components';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { setCancellationCandidates } from 'store/subscriptions/actions';
import { selectCancellationCandidates } from 'store/subscriptions/selectors';

import { sendAnalyticCancelOfferClick, trackScreenLoadCancelOffer } from 'services/Analytics';

import { useSelector } from 'hooks';
import useModals from 'modals/hooks';

import { getValuesByKey, roundToNearest } from 'helpers/utils';

import Modal from 'components/Modal';

import { CancelFlowOffers, CancelOfferAction } from 'types/subscription';

import SubscriptionsSkeleton from '../../../../components/Skeleton/Skeleton';
import useCancelSubscriptionFlow from '../../../../hooks/useCancelSubscriptionFlow';
import useDiscountModal from '../../../../hooks/useDiscountModal';
import FullScreenModalHeader from '../../../components/FullScreenModalHeader';
import SubscriptionCards from '../components/SubscriptionCards';
import * as S from '../DiscountModal/styled';

import ReturnOfferMessage from './components/ReturnOfferMessage';
import { ReturnOfferModalProps } from './types';

const ReturnModal = ({ isExtraOffer = false }: ReturnOfferModalProps) => {
    const { closeModal } = useModals();
    const { t } = useTranslation();

    const { onChangeSubscription, onResetSubscription } = useCancelSubscriptionFlow();

    const dispatch = useDispatch();

    const cancellationCandidates = useSelector(selectCancellationCandidates);

    const { currentSubscription, currencySign, discountOffer } = useDiscountModal();

    const isDiscountOfferReady = discountOffer && currentSubscription && currencySign;

    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    useEffect(() => {
        trackScreenLoadCancelOffer({ eventLabel: { subscription_id, offer: CancelFlowOffers.DiscountAfterCancel } });
    }, [subscription_id]);

    const sendAnalytics = useCallback(
        (action: CancelOfferAction) => {
            sendAnalyticCancelOfferClick({
                eventLabel: { subscription_id, offer: CancelFlowOffers.DiscountAfterCancel },
                actionEvent: action,
            });
        },
        [subscription_id]
    );

    const changeSubscription = useCallback(() => {
        const { cancelled_at, internal_cancel_at } = cancellationCandidates?.[0] ?? {};

        sendAnalytics(CancelOfferAction.Accept);
        cancelled_at || internal_cancel_at ? onResetSubscription(isExtraOffer) : onChangeSubscription(isExtraOffer);
    }, [onChangeSubscription, sendAnalytics, isExtraOffer, cancellationCandidates, onResetSubscription]);

    const onClose = useCallback(() => {
        dispatch(setCancellationCandidates(null));
        sendAnalytics(CancelOfferAction.Close);
        closeModal();
    }, [closeModal, sendAnalytics, dispatch]);

    const discount = isDiscountOfferReady
        ? roundToNearest(Math.round(100 * (1 - Number(discountOffer.price) / Number(currentSubscription.price))))
        : null;

    return (
        <Modal
            fullscreen
            isOpen
            onClose={onClose}
            customStyles={{ background: '#F3F3F8', padding: 0 }}
            withRoundedCorners={false}
            dataLocator="discountOfferContainer"
        >
            <FullScreenModalHeader onBackButtonClick={null} closeModal={onClose} />

            <div className="container-sm">
                <S.ModalContent>
                    {!isDiscountOfferReady ? (
                        <SubscriptionsSkeleton />
                    ) : (
                        <>
                            <div>
                                {!isExtraOffer && <ReturnOfferMessage />}
                                <Text medium center mb={12} type="h4" dataLocator="returnTitle">
                                    {t('subscription.discountOfferModal.returnOffer.title', { percent: discount })}
                                </Text>
                                <Text center type="large-text" dataLocator="returnDescription">
                                    {t('subscription.discountOfferModal.returnOffer.description', {
                                        percent: discount,
                                    })}
                                </Text>
                            </div>
                            <SubscriptionCards
                                currencySign={currencySign}
                                currentSubscription={currentSubscription}
                                onChangeSubscription={changeSubscription}
                                discountPrice={discountOffer.price}
                                discount={discount as number}
                                title={t('subscription.discountOfferModal.currentCard.canceledPlan.title')}
                            />
                        </>
                    )}
                </S.ModalContent>
            </div>
        </Modal>
    );
};

export default ReturnModal;
